import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import GeoContext from 'contexts/geoContext'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import debounce from 'debounce-promise'
import { getTypeaheadSuggestions, TypeAheadSearchTypes } from 'api/search'
import { KEYCODE_ENTER } from 'constants/keyCode'
import LocationSearchFieldNoOptionsMessage from '../Components/LocationSearchField/LocationSearchFieldNoOptionsMessage'
import { LOCATION_SEARCH_ERROR_MESSAGE } from 'constants/search'
import useDidChange from 'hooks/useDidChange'

export interface LocationSearchFieldProps {
  selectRef: React.MutableRefObject<any>;
  searchTypes?: Array<App.SearchType>;
  typeaheadTypes?: Array<TypeAheadSearchTypes>;
  onSelect: (searchItem: App.SearchItem) => void;
  isSecondaryLocation?: boolean;
  popularPlaces?: Array<App.SearchItem>
}

const useLocationSearchFieldProps = (props: LocationSearchFieldProps) => {
  const {
    selectRef,
    typeaheadTypes,
    searchTypes,
    onSelect,
    isSecondaryLocation,
    popularPlaces,
  } = props

  const searchState = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { currentRegionCode } = useContext(GeoContext)

  const location = isSecondaryLocation ? searchState.secondaryLocation : searchState.location
  const searchItem = isSecondaryLocation ? searchState.secondarySearchItem : searchState.searchItem

  const [resultCount, setResultCount] = useState<number>()
  const [isSettingValue, setIsSettingValue] = useState<boolean>(false)

  const hasChangedSearchItem = useDidChange(searchItem)

  // This logic is used to fire onSelect method only when state is  updated
  useEffect(() => {
    if (isSettingValue && hasChangedSearchItem && searchItem) {
      setIsSettingValue(false)
      onSelect(searchItem)
    }
  }, [isSettingValue, hasChangedSearchItem, searchItem, onSelect])

  const setValue = useCallback((newSearchItem?: App.SearchItem) => {
    if (newSearchItem === searchItem) return
    if (isSecondaryLocation) {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM, searchItem: newSearchItem })
    } else {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: newSearchItem })
    }

    if (newSearchItem) {
      setIsSettingValue(true)
    }
  }, [globalSearchDispatch, isSecondaryLocation, searchItem])

  const onPredictionLoad = useCallback(function(options: any) {
    setResultCount(options.length)
  }, [])

  const onChange = useCallback((
    changedSearchItem: App.SearchItem,
    { action }: { action: string },
  ) => {
    if (action === 'clear') {
      if (isSecondaryLocation) {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_SECONDARY_LOCATION })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_LOCATION })
      }

      return
    } else if (action === 'select-option') {
      if (isSecondaryLocation) {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_SECONDARY_LOCATION })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_LOCATION })
      }
      setValue(changedSearchItem)
    }
  }, [globalSearchDispatch, isSecondaryLocation, setValue])

  const debouncedGetPlacePredictions = useMemo(
    () => debounce((search: string, callback) => {
      getTypeaheadSuggestions(search, currentRegionCode, { types: typeaheadTypes, searchTypes }).then(options => {
        callback(options)
        onPredictionLoad(options)
      }).catch(() => callback(null))
    }, 500),
    [onPredictionLoad, currentRegionCode, typeaheadTypes, searchTypes],
  )

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === KEYCODE_ENTER) {
      if (selectRef.current.state.isLoading) {
        e.preventDefault()
      }
    }
  }

  const loadOptions = useCallback((search: string, callback) => {
    search.length > 1 ? debouncedGetPlacePredictions(search, callback) : callback(null)
  }, [debouncedGetPlacePredictions])

  const onInputChange = useCallback((newInput: string, { action }: { action: string }) => {
    if (action === 'input-change') {
      if (isSecondaryLocation) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_SECONDARY_LOCATION, location: newInput })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_LOCATION, location: newInput })
      }
    }
  }, [globalSearchDispatch, isSecondaryLocation])

  const noOptionsMessage = useMemo(() => (
    <LocationSearchFieldNoOptionsMessage
      setValue={(searchItem) => onChange(searchItem, { action: 'select-option' })}
      recentSearchItems={searchState.recentSearchItems}
      resultCount={resultCount}
      message={LOCATION_SEARCH_ERROR_MESSAGE}
      popularPlaces={popularPlaces}
    />
  ), [searchState.recentSearchItems, resultCount, popularPlaces, onChange])

  return ({
    inputValue: location,
    setValue,
    handleOnKeyDown,
    loadOptions,
    onInputChange,
    noOptionsMessage,
    onChange,
  })
}

export default useLocationSearchFieldProps
